/**
 * SessionValidator.js
 * Client-side session validation that works with Upstash Redis
 */

import EnhancedSecurity from './EnhancedSecurity';
import SessionManager from './SessionManager';

class SessionValidator {
  constructor() {
    this.apiEndpoint = 'https://devapi.rezcomm.com/device/remember';
    this.validationInterval = null;
    this.validationFrequency = 600000; // Check every 10 minutes
  }

  /**
   * Start session validation at regular intervals
   */
  startValidation() {
    // Clear any existing interval
    this.stopValidation();
    
    // Start new validation interval
    this.validationInterval = setInterval(() => {
      this.validateCurrentSession();
    }, this.validationFrequency);
    
    // Also validate immediately
    this.validateCurrentSession();
  }
  
  /**
   * Stop session validation
   */
  stopValidation() {
    if (this.validationInterval) {
      clearInterval(this.validationInterval);
      this.validationInterval = null;
    }
  }

  /**
   * Validate the current session
   * @returns {Promise<boolean>} Whether session is valid
   */
  async validateCurrentSession() {
    try {
      const sessionId = SessionManager.getSessionId();
      const userId = SessionManager.getUserId();
      
      if (!sessionId || !userId) {
        return false;
      }
      
      // First check if we have a valid RememberDevice cookie as a backup
      let rememberDeviceValid = false;
      try {
        const rememberDevice = await import('./RememberDevice').then(module => module.default);
        const deviceData = await rememberDevice.isDeviceRemembered();
        rememberDeviceValid = deviceData && deviceData.userId === userId;
      } catch (e) {
        rememberDeviceValid = false;
      }
      
      // Get current fingerprint
      const fingerprint = await EnhancedSecurity.generateAdvancedFingerprint();
      
      // Validate with server
      const response = await fetch(this.apiEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'validate',
          userId,
          sessionId,
          fingerprint
        })
      });
      
      const result = await response.json();
      
      if (!result.valid) {
        // If server validation fails but we have a valid remember device cookie,
        // we can create a new session instead of logging out
        if (rememberDeviceValid) {
          const rememberDevice = await import('./RememberDevice').then(module => module.default);
          const deviceData = await rememberDevice.isDeviceRemembered();
          await SessionManager.createSession(deviceData.userId, deviceData.email);
          return true;
        }
        
        // Session is invalid and no backup - log the user out
        this.handleInvalidSession(result.reason);
        return false;
      }
      
      return true;
    } catch (error) {
      return false;
    }
  }
  
  /**
   * Handle invalid session (logout user)
   * @param {string} reason Reason for session invalidation
   */
  handleInvalidSession(reason) {
    // Clear session data using SessionManager
    SessionManager.invalidateSession();
    
    // Show appropriate message based on reason
    if (reason === 'fingerprint_mismatch') {
      alert('Security alert: Your session appears to be used in another browser. You have been logged out for security reasons.');
    } else {
      alert('Your session has expired. Please log in again.');
    }
    
    // Redirect to login page
    window.location.href = '/login.html';
  }
}

export default new SessionValidator();